// src/lib/stock-alerte.js
//
// CE QUI MANQUE, VU PAR L'ORCHESTRATEUR (demande 2026-08-06).
//
// L'onglet Stock lui montrait une liste de produits avec, pour chacun, une
// quantité — passée en rouge sous le seuil. Deux manques :
//
//   1. Le badge « Sous seuil » n'existait QUE dans la branche admin. Le rôle
//      terrain voyait donc un chiffre rouge SANS libellé : une couleur qui
//      alerte sans dire de quoi est une devinette, pas une information.
//   2. Aucun récapitulatif. Pour savoir si quelque chose manque, il fallait
//      parcourir toute la liste — or c'est la seule question qui compte :
//      mes agents vont-ils tomber en rupture ?
//
// L'orchestrateur n'achète pas (l'onglet Courses reste réservé à l'admin) et ne
// voit AUCUN prix. Il a besoin de savoir quoi signaler, pas ce que ça coûte.
//
// Helpers PURS : aucun accès réseau, aucun DOM, aucun prix manipulé.

/** Quantité disponible d'un produit, tolérante aux formes partielles. */
function quantite(p) {
  const q = Number(p?.qtyDispo)
  return Number.isFinite(q) ? q : 0
}

/** Seuil minimal. Absent ou illisible ⇒ 0, c'est-à-dire « jamais en alerte ». */
function seuil(p) {
  const s = Number(p?.seuilMin)
  return Number.isFinite(s) && s > 0 ? s : 0
}

/**
 * Un produit est-il sous son seuil ?
 *
 * Comparaison `<=` et non `<` : à quantité ÉGALE au seuil, on est déjà au point
 * de commande — c'est précisément ce que « seuil » veut dire. Attendre de
 * passer dessous, c'est prévenir une fois qu'il est trop tard.
 *
 * Un seuil à 0 (non renseigné) ne déclenche JAMAIS : sinon tout produit épuisé
 * mais non suivi alerterait en permanence, et le signal se noierait.
 */
export function sousSeuil(p) {
  const s = seuil(p)
  if (s <= 0) return false
  return quantite(p) <= s
}

/**
 * Produits sous seuil, du plus critique au moins critique.
 *
 * « Plus critique » = le plus bas RELATIVEMENT à son seuil, pas en valeur
 * absolue : 2 draps pour un seuil de 10 est plus grave que 40 dosettes de café
 * pour un seuil de 48, alors que 2 < 40. Comparer les quantités brutes mettrait
 * le café en tête et les draps en bas de liste.
 */
export function produitsSousSeuil(produits) {
  const liste = Array.isArray(produits) ? produits : []
  return liste
    .filter(sousSeuil)
    .map((p, i) => ({ p, i, ratio: quantite(p) / seuil(p) }))
    .sort((a, b) => (a.ratio === b.ratio ? a.i - b.i : a.ratio - b.ratio))
    .map((x) => x.p)
}

/**
 * Phrase de contrôle en tête d'onglet — même grammaire que les autres écrans de
 * l'orchestrateur : dire d'emblée s'il y a quelque chose à faire.
 *
 * @returns {{ texte: string, alerte: boolean, nb: number }}
 */
export function resumeStock(produits) {
  const liste = Array.isArray(produits) ? produits : []
  const bas = produitsSousSeuil(liste)
  if (!liste.length) return { texte: 'Aucun consommable suivi.', alerte: false, nb: 0 }
  if (!bas.length) return { texte: 'Tout est approvisionné.', alerte: false, nb: 0 }

  const n = bas.length
  const noms = bas.slice(0, 2).map((p) => p?.nom).filter(Boolean)
  const reste = n - noms.length
  const detail = noms.length
    ? ` — ${noms.join(', ')}${reste > 0 ? ` et ${reste} autre${reste > 1 ? 's' : ''}` : ''}`
    : ''
  return {
    texte: `${n} produit${n > 1 ? 's' : ''} presque épuisé${n > 1 ? 's' : ''}${detail}`,
    alerte: true,
    nb: n,
  }
}
